import { createContext, useContext, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface SearchContextType {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  performSearch: (term: string) => void;
  clearSearch: () => void;
  searchHighlights: (term: string) => Promise<any[]>;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const SearchProvider = ({ children }: { children: ReactNode }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const performSearch = (term: string) => {
    setSearchTerm(term);
    if (term.trim()) {
      // Navigate to practice page with search, and check for highlights
      navigate(`/dashboard/practice?search=${encodeURIComponent(term.trim())}`);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const searchHighlights = async (term: string) => {
    if (!term.trim()) return [];
    
    try {
      const { data, error } = await supabase
        .from('user_highlights')
        .select(`
          *,
          questions!inner(
            id,
            question_text,
            category_id,
            categories(name)
          )
        `)
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .ilike('highlighted_text', `%${term.trim()}%`);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching highlights:', error);
      return [];
    }
  };

  return (
    <SearchContext.Provider value={{
      searchTerm,
      setSearchTerm,
      performSearch,
      clearSearch,
      searchHighlights
    }}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};